/* eslint-disable max-len */
import type { PricingIndexProfile } from './pricing-index-profile';
import {
  COMPARABLE_GROUPS_SEED,
  DEFAULT_SETTINGS,
  INDEX_HISTORY_SEED,
  INDICES_SEED,
  PARTS_SEED,
  PURCHASES_SEED,
  SEED_CLAUSE_REGISTER,
} from '../craig-engine-data';
import {
  SUPPLIERS,
  BID_SKUS,
  ASSUMPTIONS,
  DECISIONS,
  CONTRACT_BASKET_24_4_5,
  IDENTITY_FACTS,
} from '../mock-data';
import { USER_STORIES } from '../user-stories-ledger';

/**
 * MEPPI_PROFILE — the original Mitsubishi Electric Power Products demo
 * profile. References the existing craig-engine-data seeds verbatim so
 * historical behaviour and the full spec suite are unchanged. All
 * customer-specific copy strings continue to live in the component HTML
 * literals; the strings here are surfaced for parity with DEMO_PROFILE and
 * are tested via brand-string assertions in demo-profile.spec.ts.
 */
export const MEPPI_PROFILE: PricingIndexProfile = {
  profileId: 'meppi',
  displayName: 'Mitsubishi Electric Power Products (MEPPI)',

  settings: { ...DEFAULT_SETTINGS },
  indices: INDICES_SEED.map(i => ({ ...i })),
  comparableGroups: COMPARABLE_GROUPS_SEED.map(g => ({ ...g })),
  parts: PARTS_SEED.map(p => ({ ...p })),
  purchases: PURCHASES_SEED.map(p => ({ ...p })),
  indexHistory: INDEX_HISTORY_SEED.map(h => ({ ...h })),
  clauseRegister: SEED_CLAUSE_REGISTER.map(c => ({ ...c })),

  // Contract-clause view data — reference the existing mock-data constants
  // verbatim so the MEPPI route stays byte-identical to before.
  contractClauseSuppliers: SUPPLIERS,
  contractClauseBidSkus: BID_SKUS,
  contractClauseAssumptions: ASSUMPTIONS,
  contractClauseDecisions: DECISIONS,
  contractClauseBasket: CONTRACT_BASKET_24_4_5,
  contractClauseIdentity: {
    customer: {
      fullName: IDENTITY_FACTS.meppi.fullName,
      hq: IDENTITY_FACTS.meppi.hq,
      parent: IDENTITY_FACTS.meppi.parent,
      sourceUrl: IDENTITY_FACTS.meppi.sourceUrl,
    },
    spendkey: {
      domain: IDENTITY_FACTS.spendkey.domain,
      sourceUrl: IDENTITY_FACTS.spendkey.sourceUrl,
    },
  },

  strings: {
    pageSubtitle: 'BLS-grounded pricing-index dashboard',
    bidName: DEFAULT_SETTINGS.bidName,
    escalationClauseLabel: '§24.4 escalation',
    basketReconcilerLabel: '§24.4.5 basket',
    partialClauseLabel: '§24.4 partial',
    escalationMechanismName: '§24.4',
    contractName: 'Authority Contract',
    attachmentReference: 'Attachment B v1',
    customerDisplayName: 'Mitsubishi Electric Power Products, Inc.',
    customerSourceUrl: 'https://www.meppi.com/about-us',
    customerFactualStatement:
      'Dashboard prepared for Mitsubishi Electric Power Products, Inc.',
    excelTemplatePrefix: 'meppi-dashboard-template',
    resetSeedDescription: 'MEPPI seed',
    resetSeedDatasetDescriptor: 'MEPPI demo dataset',
    passthroughFullClauseId: '§24.4',
    passthroughPartialClauseId: '§24.4 partial',
    basketReconcilerActualLabel: '§24.4.5 basket — actual (% since base)',
    partsMasterRecoveryColumnTooltip:
      'Contractual basis for cost recovery (e.g. \'Section 24.4 escalation\'). Used by the Recovery Calculator to attach a basis to each invoice line.',

    // Contract-clause view strings (MEPPI — historical wording)
    contractClauseTabSub: '§24.4.5 basket',
    contractClauseTabBarSub: '§24.4.5 basket reconciler',
    contractClauseOrientationTooltip:
      'Alternative view: the §24.4.5 contract-clause basket reconciler. Same model, different lens — useful when the conversation is contractual rather than analytical.',
    contractClauseTabTooltip:
      'Alternative legacy view: §24.4.5 contract-clause basket reconciler. Same data, contract-clause lens.',
    contractClauseHeaderSubtitlePrefix: '§24.4.5 basket reconciler',
    contractClauseHeaderSubtitleSuffix: 'prototype workspace',
    contractClauseBasePriceTooltip:
      'Contract base price per unit at the base date — the dollar baseline the §24.4.5 basket movement is applied against to derive the justified increase and any pending claim\'s dollar value. Editing it re-scales every downstream $ figure on this dashboard.',
    contractClauseNextReviewTooltip:
      'Next scheduled price-review date under §24.4. Until this date arrives, any supplier-initiated repricing is voluntary on Spendkey\'s side. Drives the \'days until review\' signal card and gates the negotiation posture commentary.',
    contractClausePendingClaimTooltip:
      'The %-price-increase the supplier has currently asked for, off-cycle. Used in three places: (1) compared against the §24.4.5 basket movement to flag over- or under-claiming; (2) converted to a dollar figure via Base price; (3) pre-fills the negotiation reconciler input below.',
    contractClauseHeroTitle: '§24.4.5 basket',
    contractClauseHeroTooltip:
      'Solid line = real BLS data weighted per Attachment B v1 rows 10-14. Dashed line = forward projection under the selected scenario.',
    contractClauseBasketSectionTitle: 'Agreed §24.4.5 indices',
    contractClauseWeightInputTooltip:
      'Weight of this index in this supplier\'s §24.4.5 basket. Indexes the share of the BOM that this commodity drives. All weights for a supplier should sum to 100% — the warning icon flags if they don\'t. Changes recompute the justified basket movement live.',
    contractClauseReconcilerIntro:
      'Supplier is asking for a price increase. What does the §24.4.5 basket justify?',
    contractClauseReconcilerTooltip:
      'Plug in the supplier\'s claimed increase. The tool computes what the §24.4.5 basket actually justifies, and shows the gap in pp and $ per unit.',
    contractClauseMathIndexTooltip:
      'BLS series code making up the §24.4.5 basket. Click header to sort.',
    contractClauseMathWeightTooltip:
      'Weight in the basket per Attachment B v1. The five weights sum to 100%. Click header to sort.',
    contractClauseMovementSinceBaseLabel: '§24.4.5 basket movement since base',
    contractClausePassThroughTooltip:
      'Customer pays a fraction of the full escalation, depending on §4.11 qualifying-share rules and §24.4.10 change-order exclusions.',
    contractClauseBidSkusMatchedIndexTooltip:
      'BLS series this SKU maps to under the §24.4.5 basket. Click header to sort.',
    contractClauseBidSkusCoverageTooltip:
      'Whether this SKU is covered by an agreed §24.4.5 index. Uncovered → no contractual escalation route. Click header to sort.',
    contractClauseEmptyStateNote:
      'Per-supplier indices typically come from the signed §24.4 schedule. Until that is loaded, this slot stays in TBD state by design — the dashboard never invents supplier data.',
    contractClauseUserGuideText:
      'alternative §24.4.5 basket-reconciler lens; same data, contract-clause math.',
    contractClauseFooterDisclaimer:
      'Prototype workspace — index values are real BLS data; supplier and bid SKU records are placeholders pending MEPPI source data. Math is the §24.4.5 basket (Attachment B v1 rows 10–14).',
    contractClauseCustomerSourceLinkLabel: 'MEPPI (corporate identity)',
    basketKpiUnitLabel: 'per car set',
  },

  userStoriesLedger: USER_STORIES,
  userStoriesLedgerShowStatusBadges: true,

  showUserStoriesLedger: true,
  showContractClauseView: true,
  showCustomerIdentityFooter: true,
};
